import { useIsInstallSourceTrackingEnabled } from '@/app-shell/flags/desktop-flags';
import { installSourceContextAtom } from '@/atoms/install-source';
import { applyInstallSourceContext } from '@/lib/analytics/install-source-context';
import { isTauri } from '@/tauri/utils';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

/**
 * Resolves the install-source analytics context once after the analytics
 * providers mount and publishes it to `installSourceContextAtom`. Gated by the
 * `release-apps-install-source-tracking-desktop` Flagsmith flag (APP-5958).
 *
 * Guard order is deliberate: the telemetry axis and the platform check run *before* the
 * flag so the F1 dead-code-elimination guarantee never depends on remote flag
 * state, and the airgapped build (which can't reach Flagsmith) is unaffected.
 * Resolution runs at most once per session — Snowplow `addGlobalContexts`
 * appends, so re-running would duplicate the entity on every event.
 */
export function useInitInstallSourceContext(): void {
  const enabled = useIsInstallSourceTrackingEnabled();
  const setInstallSourceContext = useSetAtom(installSourceContextAtom);
  const hasRunRef = useRef(false);

  useEffect(() => {
    // TELEMETRY-GATE — the suppression guarantee must not depend on a remote flag.
    if (!isSaasTelemetryEnabled) return;
    if (!isTauri()) return; // install source is a desktop-only dimension.
    if (!enabled) return; // release gate.
    if (hasRunRef.current) return; // resolve once per session.
    hasRunRef.current = true;

    void applyInstallSourceContext().then((context) => {
      if (context) setInstallSourceContext(context);
    });
  }, [enabled, setInstallSourceContext]);
}

/**
 * Null-rendering wrapper so `_app.tsx` can mount the init hook alongside the
 * other post-provider sync components (e.g. `OpenFeatureContextSync`).
 */
export function InstallSourceContextSync() {
  useInitInstallSourceContext();
  return null;
}
